import { Card, Colors, Icon, Tabs, Text } from '@amelio/ui-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ALL_EVALUATORS,
    CUSTOM_PERIOD,
    SkillsetAssessmentFilters,
    type SkillsetPeriod,
} from './SkillsetAssessmentFilters';
import { SkillsetAssessmentPrintButton } from './SkillsetAssessmentPrintButton';
import { SkillsetCategorySection } from './SkillsetCategorySection';
import { SkillsetProficiencyLegend } from './SkillsetProficiencyLegend';
import { readTargetStepTitle, readTargetStep } from '../GrowthTab/targetPosition';

import type { AssessmentHistoryModalRef } from '@/pages/shared/HistoryModals/AssessmentHistoryModal';
import type { SkillProficiencyModalRef } from '@/pages/shared/HistoryModals/SkillProficiencyModal';
import type {
    ProficiencyLevel,
    SkillItem,
    SkillsetCompetencySet,
} from '@/types/performance-profile';

import { isRefusedError } from '@/api/api';
import { useGetUserById } from '@/api/directory';
import {
    useGetEmployeeCompetencyAssignment,
    useGetEmployeeGrowth,
    useGetSkillsetAssessment,
    useGetSkillsetEvaluators,
    useUpdateSkillProficiency,
} from '@/api/performance-profile';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { AssessmentHistoryModal } from '@/pages/shared/HistoryModals/AssessmentHistoryModal';
import { SkillProficiencyModal } from '@/pages/shared/HistoryModals/SkillProficiencyModal';
import { useProficiencyLevelPresentation } from '@/pages/shared/HistoryModals/useProficiencyLevelPresentation';
import { toIsoDate } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';

interface SkillsetAssessmentCardProps {
    employeeId: string;
    /**
     * The lens the page holds, rather than one this card owns: the performance score above it is
     * the average of these very competencies, so the two read the same evaluations or neither of
     * them means anything.
     */
    evaluatorId: string;
    onEvaluatorChange: (evaluatorId: string) => void;
    period: SkillsetPeriod;
    onPeriodChange: (period: SkillsetPeriod) => void;
}

/** The empty state's illustration: the disc idiom the Notes tab already uses, in brand tokens. */
const AssignProfileIllustration = () => (
    <div
        aria-hidden
        className={
            'from-primary/15 to-growth-arc-start/25 flex size-16 items-center justify-center rounded-full bg-gradient-to-br'
        }
    >
        <Icon
            name={'users-profile-add'}
            size={'lg'}
            color={Colors.PRIMARY}
        />
    </div>
);

export const SkillsetAssessmentCard = ({
    employeeId,
    evaluatorId,
    onEvaluatorChange,
    period,
    onPeriodChange,
}: SkillsetAssessmentCardProps) => {
    const { t } = useTranslation();
    const [openCategoryIds, setOpenCategoryIds] = useState<Set<string>>(new Set());
    const [competencySet, setCompetencySet] = useState<SkillsetCompetencySet>('CurrentPosition');

    const { data: employeeUser } = useGetUserById({ userId: employeeId });
    const { data: competencyAssignment } = useGetEmployeeCompetencyAssignment(employeeId);
    // The growth read is refused on a manager's own profile; the desired position is then not
    // offered, and the server would refuse its competencies for the same reason.
    const { data: growth, error: growthError } = useGetEmployeeGrowth(employeeId);
    const isGrowthRefused = isRefusedError(growthError);
    const shownCompetencySet: SkillsetCompetencySet = isGrowthRefused
        ? 'CurrentPosition'
        : competencySet;
    const isDesiredPosition = shownCompetencySet === 'DesiredPosition';

    const employeeName = employeeUser
        ? `${employeeUser.firstName} ${employeeUser.lastName}`.trim()
        : '';
    // The desired position is the target step, as the server reads it: a profile on the current
    // step is the position held, never the one aimed for.
    const shownProfileName = isDesiredPosition
        ? readTargetStep(growth)?.competencyProfileName
        : competencyAssignment?.profileName;
    const competencyProfileName = shownProfileName ? getLocalizedText(shownProfileName) : null;

    // The chosen evaluator can leave the list -- another employee's profile, or a 360 reviewer kept
    // anonymous on one's own -- and then filters nothing: the card falls back to every evaluation
    // rather than asking for levels nobody on this profile gave.
    const { data: evaluators } = useGetSkillsetEvaluators(employeeId);
    const shownEvaluatorId =
        evaluators && !evaluators.some((evaluator) => evaluator.id === evaluatorId)
            ? ALL_EVALUATORS
            : evaluatorId;

    // Only the custom period narrows by date: "Tous" reads every evaluation, and a predefined
    // period is already named by its id. Sending the two fields whatever the menu says would cut
    // the read down to the range they happen to hold, and every competency would read as
    // unassessed.
    const isCustomPeriod = period.filterId === CUSTOM_PERIOD;

    const { data, isLoading, isError } = useGetSkillsetAssessment(employeeId, {
        filterId: period.filterId,
        fromDate: isCustomPeriod ? toIsoDate(period.fromDate) : undefined,
        toDate: isCustomPeriod ? toIsoDate(period.toDate) : undefined,
        evaluatorId: shownEvaluatorId === ALL_EVALUATORS ? undefined : shownEvaluatorId,
        competencySet: shownCompetencySet,
    });

    const {
        displayLevels: legendLevels,
        proficiencyLevelMap,
        getProficiencyLabel: getLabel,
        isLoading: isLevelsLoading,
    } = useProficiencyLevelPresentation();

    const getProficiencyLabel = (level: Parameters<typeof getLabel>[0]) =>
        getLabel(level, t('performanceProfile.skillsetAssessment.proficiencyLevels.na'));

    // One legend, read on screen and printed in the document, so the icon shown for a level is
    // never the one the export decided on its own.
    const legendEntries = legendLevels.map((level) => {
        const presentation = proficiencyLevelMap[level];
        return {
            id: level,
            label: getProficiencyLabel(level),
            iconName: presentation?.icon,
            iconUrl: presentation?.iconUrl,
            iconColor: presentation?.color,
        };
    });

    const updateSkillProficiencyMutation = useUpdateSkillProficiency(employeeId);
    const proficiencyModalRef = useRef<SkillProficiencyModalRef>(null);
    const assessmentHistoryModalRef = useRef<AssessmentHistoryModalRef>(null);

    const toggleCategory = (categoryId: string) => {
        setOpenCategoryIds((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const handleEditSkill = (skill: SkillItem) => {
        proficiencyModalRef.current?.open(skill);
    };

    const handleViewAssessmentHistory = (skill: SkillItem) => {
        assessmentHistoryModalRef.current?.open(skill);
    };

    const handleSaveProficiency = (
        skillId: string,
        proficiency: ProficiencyLevel,
        comment: string,
        cycleInstanceId: string,
    ) => {
        updateSkillProficiencyMutation.mutate(
            { skillId, proficiency, comment, cycleInstanceId },
            {
                onSuccess: () => {
                    proficiencyModalRef.current?.close();
                },
            },
        );
    };

    return (
        <Card
            className={
                'ds-mt-lg relative overflow-visible !rounded-2xl border border-white bg-white/45 !py-[28px] md:!px-[33px]'
            }
            dataTestId={'skillset-assessment-card'}
        >
            <div className={'flex flex-wrap items-center justify-between gap-4'}>
                <Text
                    as={'h3'}
                    weight={'medium'}
                    className={'text-navy text-[20px] leading-tight'}
                >
                    {t('performanceProfile.skillsetAssessment.title')}
                </Text>

                <SkillsetAssessmentPrintButton
                    employeeName={employeeName}
                    competencyProfileName={competencyProfileName}
                    categories={data?.categories ?? []}
                    legend={legendEntries}
                />
            </div>
            <SkillsetAssessmentFilters
                employeeId={employeeId}
                evaluatorId={shownEvaluatorId}
                onEvaluatorChange={onEvaluatorChange}
                period={period}
                onPeriodChange={onPeriodChange}
            />

            {/* The tabs, the bands and the legend share one opaque surface: read straight on the
                card's tinted wash, a pale band and the text it carries lose most of their
                separation from the background. */}
            <div className={'ds-mt-lg ds-p-lg rounded-xl bg-white/90'}>
                {!isGrowthRefused && (
                    <Tabs
                        items={[
                            {
                                key: 'CurrentPosition',
                                label: t(
                                    'performanceProfile.skillsetAssessment.currentPositionSkills',
                                ),
                            },
                            {
                                key: 'DesiredPosition',
                                label: t(
                                    'performanceProfile.skillsetAssessment.desiredPositionSkills',
                                ),
                            },
                        ]}
                        activeKey={competencySet}
                        onChange={(key) => setCompetencySet(key as SkillsetCompetencySet)}
                        variant={'segmented'}
                        ariaLabel={t('performanceProfile.skillsetAssessment.competencySet')}
                        // The panel lays its children out as a stretching flex column, so the tab
                        // group's `inline-flex` is pulled to the full panel width and its two tabs,
                        // sized by their text, leave the rest as an empty band. The row is bounded
                        // instead, at the width of the reference drawing.
                        className={'w-full max-w-[520px]'}
                        // The two labels differ in length by half, so tabs sized by their own text
                        // read as two unrelated controls. They share the row instead, and the
                        // longer label wraps inside its half rather than stretching it.
                        equalWidth
                        // Below 768px the row becomes a dropdown rather than two squeezed tabs: these
                        // two French labels need three lines, which the drawing's fixed-height tab
                        // clips. Deviation from the drawing, assumed on 2026-09-17.
                        collapseBreakpoint={'md'}
                        dataTestId={'skillset-assessment-competency-set'}
                    />
                )}

                {isLoading && (
                    <Text
                        color={Colors.BASE_400}
                        className={'mt-4'}
                        data-testid={'skillset-assessment-loading'}
                    >
                        {t('performanceProfile.loading')}
                    </Text>
                )}

                {/* A read that failed says nothing about what was assessed. The server answers an
                    employee nobody assessed with an empty list, never an error, so an error here is
                    always a failure to read -- and telling a manager "no competency assessed" would
                    turn an outage into a statement about their employee. */}
                {!isLoading && isError && (
                    <Text
                        className={'text-error mt-4'}
                        data-testid={'skillset-assessment-error'}
                    >
                        {t('performanceProfile.skillsetAssessment.loadError')}
                    </Text>
                )}

                {!isLoading && !isError && (
                    <div className={'ds-mt-lg gap-gap-md flex flex-col'}>
                        {data?.categories && data.categories.length > 0 ? (
                            data.categories.map((category) => (
                                <SkillsetCategorySection
                                    key={category.id}
                                    category={category}
                                    levelColor={
                                        category.proficiency
                                            ? proficiencyLevelMap[category.proficiency.id]?.color
                                            : undefined
                                    }
                                    proficiencyLabel={
                                        category.proficiency
                                            ? getProficiencyLabel(category.proficiency.id)
                                            : ''
                                    }
                                    isOpen={openCategoryIds.has(category.id)}
                                    onToggle={() => toggleCategory(category.id)}
                                    onEditSkill={handleEditSkill}
                                    onViewAssessmentHistory={handleViewAssessmentHistory}
                                />
                            ))
                        ) : isDesiredPosition ? (
                            <EmptyState
                                message={t(
                                    readTargetStepTitle(growth)
                                        ? 'performanceProfile.skillsetAssessment.desiredPositionWithoutProfile'
                                        : 'performanceProfile.skillsetAssessment.noDesiredPosition',
                                )}
                                className={'py-10'}
                            />
                        ) : (
                            // No action here: this state follows the *filtered* assessment, so it
                            // also appears for an employee who has a profile whose skills nobody
                            // assessed in the chosen period, and it disappears on the
                            // desired-position tab. The profile card above tracks the assignment
                            // itself and carries the one offer.
                            <EmptyState
                                message={t('performanceProfile.skillsetAssessment.emptyMessage')}
                                illustration={<AssignProfileIllustration />}
                                className={'py-10'}
                            />
                        )}
                    </div>
                )}

                <SkillsetProficiencyLegend
                    entries={legendEntries}
                    isLoading={isLevelsLoading}
                />
            </div>

            <SkillProficiencyModal
                ref={proficiencyModalRef}
                onSave={handleSaveProficiency}
                isSaving={updateSkillProficiencyMutation.isPending}
                dataTestId={'skill-proficiency-modal'}
            />

            <AssessmentHistoryModal
                ref={assessmentHistoryModalRef}
                employeeId={employeeId}
                dataTestId={'assessment-history-modal'}
            />
        </Card>
    );
};
